import { useContext, useEffect } from "react";
import Layout from "@wrappers/layouts/main-layout/Layout";
import { useClearGridData, useGetModulesData } from "@hooks/AllCustomHooks";
import KeywordsFromManual from "./KeywordsFromManual";
import { createDatasource } from "@ts/retrieve-data";
import {
    agBtnExport,
    setPillBadges,
    resetPillBadges,
    getActiveModule,
} from "@ts/utils";
import { getDateObj } from "@ts/date-range";
import { mainContext } from "@ts/constants";
import { useSelector, useDispatch } from "react-redux";
import { updateTitle } from "@features/titleSlice";
import { updateActiveTab } from "@features/activeTabSlice";
import { updateActiveModule } from "@features/activeModuleSlice";
import { setIsLoader } from "@features/loaderSlice";
import {
    IChannel,
    IMainContext,
    Keyword,
    RevinitiRecommendation,
    SubModule,
    TActiveClientId,
} from "@ts/common-types";
import TabsComponent from "@components/tabs/TabsComponent";
import {
    setManualRecommendationData,
    setAutoRecommendationData,
    setRevinitiRecommendationData,
} from "@features/gridDataSlice";
import { useLocation } from "react-router-dom";
import AddToAdgroupModal from "@/components/modals/AddToAdgroupModal";

const KeywordsRecommendations = () => {
    const clearGridData = useClearGridData();
    const {
        dates,
        DateRange,
        manualRecommendationsGridApi,
        revinitiRecommendationsGridApi,
        autoRecommendationsGridApi,
        setManualRecommendationsLoading,
        setAutoRecommendationsLoading,
        setRevinitiRecommendationsLoading,
    } = useContext<IMainContext>(mainContext);
    const activeClientId = useSelector(
        (state: TActiveClientId) => state.activeClientId.value
    );
    const manualRecommendationData = useSelector(
        (state: any) => state.manualRecommendationData.value
    );
    const showAddToAdgroupModal = useSelector(
        (state: any) => state.addToAdgroupModalData.display
    );
    const addToAdgroupModalData = useSelector(
        (state: any) => state.addToAdgroupModalData.value
    );
    const dispatch = useDispatch();
    const { pathname } = useLocation();
    const loaderCount = useGetModulesData({ type: "loader-count" });
    let loader = 0;

    const incrementLoader = () => {
        loader = loader + 1;
        if (loader === loaderCount) {
            dispatch(setIsLoader(false));
        }
    };

    const channel = useSelector((state: IChannel) => state.channel.value);
    const activeModule = getActiveModule().activeModule;

    const onBtnExport = () => {
        if (activeModule === SubModule.KEYWORD_MANUAL) {
            agBtnExport(manualRecommendationsGridApi);
        } else if (activeModule === SubModule.KEYWORD_REVINITI) {
            agBtnExport(revinitiRecommendationsGridApi);
        } else if (activeModule === SubModule.KEYWORD_AUTO) {
            agBtnExport(autoRecommendationsGridApi);
        }
    };

    const populateKeywordsData = async () => {
        return await createDatasource("keyword-manual-recommendation", {
            dateObj: getDateObj(),
        });
    };

    const populateAutoKeywordsData = async () => {
        return await createDatasource("keyword-auto-recommendation", {
            dateObj: getDateObj(),
        });
    };

    const populateRecommendationsData = async () => {
        return await createDatasource("reviniti-recommendation", {
            dateObj: getDateObj(),
        });
    };

    useEffect(() => {
        clearGridData();
        dispatch(updateTitle("Keywords"));
        dispatch(updateActiveTab("keyword-manual-recommendation"));
        dispatch(updateActiveModule("keywords manual"));
        dispatch(setIsLoader(true));
        setManualRecommendationsLoading(true);
        setAutoRecommendationsLoading(true);
        setRevinitiRecommendationsLoading(true);
        dispatch(setRevinitiRecommendationData(null));
        dispatch(setManualRecommendationData(null));
        dispatch(setAutoRecommendationData(null));
        // eslint-disable-next-line
        loader = 0;
        resetPillBadges();

        populateKeywordsData().then((keywords) => {
            setPillBadges("manual-badge", (keywords as Keyword[]).length);
            dispatch(setManualRecommendationData(keywords as Keyword[]));
            setManualRecommendationsLoading(false);
            incrementLoader();
        });

        populateAutoKeywordsData().then((keywords) => {
            setPillBadges("auto-badge", (keywords as Keyword[]).length);
            dispatch(setAutoRecommendationData(keywords as Keyword[]));
            setAutoRecommendationsLoading(false);
            incrementLoader();
        });

        populateRecommendationsData().then((recommendations) => {
            setPillBadges(
                "reviniti-badge",
                (recommendations as Array<RevinitiRecommendation>).length
            );
            dispatch(
                setRevinitiRecommendationData(recommendations as Array<Keyword>)
            );
            setRevinitiRecommendationsLoading(false);
            incrementLoader();
        });
    }, [activeClientId, dates, DateRange, dispatch, channel]);

    // While changing tabs, rows are getting set out of order, so we're having to redraw rows.
    useEffect(() => {
        manualRecommendationData?.length &&
            manualRecommendationsGridApi?.redrawRows();
    }, [manualRecommendationData, manualRecommendationsGridApi]);

    return (
        <>
            <Layout>
                <div className="main-content-body subheader-fixed">
                    <div className="tabbable-line">
                        <TabsComponent onBtnExport={onBtnExport} />
                        {pathname === "/keyword-recommendations" && (
                            <KeywordsFromManual />
                        )}
                    </div>
                </div>
                {showAddToAdgroupModal ? (
                    <AddToAdgroupModal
                        {...{ showAddToAdgroupModal, addToAdgroupModalData }}
                    />
                ) : null}
            </Layout>
        </>
    );
};

export default KeywordsRecommendations;
